"use client";

import { Bell, BellOff, Loader2 } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { Button } from "@/components/ui/button";
import type { NotificationItem } from "@/types";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/notifications")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.notifications)) {
          setNotifications(data.notifications);
        }
      })
      .catch(() => toast.error("Unable to load notifications."))
      .finally(() => setLoading(false));
  }, []);

  async function markRead(id: string) {
    setNotifications((list) =>
      list.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    try {
      await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
    } catch {
      // best-effort
    }
  }

  async function markAllRead() {
    setNotifications((list) => list.map((n) => ({ ...n, read: true })));
    try {
      await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ readAll: true })
      });
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Unable to update notifications.");
    }
  }

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <DashboardShell title="Notifications">
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading notifications…
        </div>
      ) : notifications.length === 0 ? (
        <div className="amazon-card py-12 text-center">
          <BellOff className="mx-auto h-10 w-10 text-slate-400" />
          <p className="mt-3 font-bold">No notifications yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Order updates and promotions will appear here.
          </p>
          <Link href="/search" className="amazon-btn-primary mt-5 inline-flex">
            Start shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {unread > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {unread} unread notification{unread !== 1 ? "s" : ""}
              </p>
              <Button variant="outline" size="sm" onClick={markAllRead}>
                <Bell className="h-3.5 w-3.5" />
                Mark all read
              </Button>
            </div>
          )}
          {notifications.map((n) => (
            <article
              key={n.id}
              className={`amazon-card ${!n.read ? "border-l-4 border-l-amazon-orange" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase text-slate-500">{n.type}</p>
                  <h3 className="font-bold">{n.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{n.body}</p>
                  {n.link && (
                    <Link
                      href={n.link}
                      className="mt-2 inline-block text-xs text-amazon-teal hover:underline"
                    >
                      View details →
                    </Link>
                  )}
                  <p className="mt-2 text-xs text-slate-400">
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                </div>
                {!n.read && (
                  <button
                    type="button"
                    className="shrink-0 text-xs text-amazon-teal hover:underline"
                    onClick={() => markRead(n.id)}
                  >
                    Mark read
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
